const adminApiBaseUrl = (
  process.env.REACT_APP_ADMIN_API_BASE_URL ?? "http://localhost:8787"
).replace(/\/+$/, "");

export class AdminApiError extends Error {
  constructor(message, status) {
    super(message);
    this.name = "AdminApiError";
    this.status = status;
  }
}

async function adminRequest(path, options = {}) {
  const response = await fetch(`${adminApiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...options.headers,
    },
  });

  let payload = null;
  try {
    payload = await response.json();
  } catch {
    // Status remains sufficient for the deliberately minimal admin UI.
  }

  if (!response.ok) {
    throw new AdminApiError(
      payload?.error?.message ?? "The admin request could not be completed.",
      response.status,
    );
  }
  return payload;
}

export function loginAdmin(username, password) {
  return adminRequest("/api/admin/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  }).then((result) => {
    if (result?.success !== true) throw new AdminApiError("Login failed.", 502);
    return result;
  });
}

export function fetchAdminOrders(start) {
  return adminRequest(`/api/admin/orders?start=${encodeURIComponent(start)}`).then(
    (result) => {
      if (result?.success !== true || !Array.isArray(result.orders)) {
        throw new AdminApiError("Orders could not be loaded.", 502);
      }
      return result;
    },
  );
}

export function forceAdminEmails(orderIds) {
  return adminRequest("/api/admin/force-emails", {
    method: "POST",
    body: JSON.stringify({ orderIds }),
  }).then((result) => {
    if (result?.success !== true) {
      throw new AdminApiError("Emails could not be sent.", 502);
    }
    return result;
  });
}

export function sendAdminPreorderEmails(orderIds) {
  return adminRequest("/api/admin/preorder-emails", {
    method: "POST",
    body: JSON.stringify({ orderIds }),
  }).then((result) => {
    if (result?.success !== true) {
      throw new AdminApiError("Preorder emails could not be sent.", 502);
    }
    return result;
  });
}

export function fetchAdminDiscounts() {
  return adminRequest("/api/admin/discounts").then((result) => {
    if (result?.success !== true || !Array.isArray(result.discounts)) {
      throw new AdminApiError("Discount codes could not be loaded.", 502);
    }
    return result.discounts;
  });
}

export function createAdminDiscount(discount) {
  return adminRequest("/api/admin/discounts", {
    method: "POST",
    body: JSON.stringify(discount),
  }).then((result) => {
    if (
      result?.success !== true ||
      typeof result.discount?.id !== "string" ||
      typeof result.discount?.code !== "string"
    ) {
      throw new AdminApiError("Discount code could not be created.", 502);
    }
    return result;
  });
}

export function revokeAdminDiscount(discountId) {
  return adminRequest("/api/admin/discounts/revoke", {
    method: "POST",
    body: JSON.stringify({ discountId }),
  }).then((result) => {
    if (result?.success !== true || typeof result.code !== "string") {
      throw new AdminApiError("Discount code could not be revoked.", 502);
    }
    return result;
  });
}

export function formatAdminAmount(amountPaise, currency = "INR") {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: amountPaise % 100 === 0 ? 0 : 2,
  }).format(amountPaise / 100);
}
